import React, { useMemo } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Space,
  Table,
  Tag,
  Progress,
  Spin,
  Alert,
  Button,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import dayjs from "dayjs";
import { reportsService } from "../services/reportsService";
import { bookingService } from "../services/bookingService";

const { Title } = Typography;

// Define colors for pie chart
const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff7300", "#8dd1e1"];

const DashboardPage: React.FC = () => {
  const today = dayjs().format("DD/MM/YYYY");
  const startDate = dayjs().subtract(6, "day").format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");
  const queryClient = useQueryClient();

  // ດຶງຂໍ້ມູນລາຍງານຕ່າງໆ
  const {
    data: revenueData,
    isLoading: revenueLoading,
    error: revenueError,
  } = useQuery(
    ["revenue-report", startDate, endDate],
    () =>
      reportsService.getRevenueReport({ startDate, endDate, groupBy: "day" }),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const {
    data: roomUsageData,
    isLoading: roomUsageLoading,
    error: roomUsageError,
  } = useQuery(
    ["room-usage-report", startDate, endDate],
    () => reportsService.getRoomUsageReport({ startDate, endDate }),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const {
    data: employeePerformanceData,
    isLoading: employeePerformanceLoading,
    error: employeePerformanceError,
  } = useQuery(
    ["employee-performance-report", startDate, endDate],
    () => reportsService.getEmployeePerformanceReport({ startDate, endDate }),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  const {
    data: customerAnalysisData,
    isLoading: customerAnalysisLoading,
    error: customerAnalysisError,
  } = useQuery(
    ["customer-analysis-report", startDate, endDate],
    () => reportsService.getCustomerAnalysisReport({ startDate, endDate }),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    }
  );

  // ດຶງຂໍ້ມູນການຈອງມື້ນີ້
  const {
    data: todayBookingsData,
    isLoading: todayBookingsLoading,
    error: todayBookingsError,
  } = useQuery(["today-bookings"], () => bookingService.getTodayBookings(), {
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  // Mutation ສຳລັບຢືນຢັນແລະຍົກເລີກການຈອງ
  const confirmBookingMutation = useMutation(
    (id: string) => bookingService.confirmBooking(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["today-bookings"]);
      },
      onError: (error) => {
        console.error("Failed to confirm booking:", error);
      },
    }
  );

  const cancelBookingMutation = useMutation(
    (id: string) =>
      bookingService.cancelBooking(id, "Cancelled from dashboard"),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["today-bookings"]);
      },
      onError: (error) => {
        console.error("Failed to cancel booking:", error);
      },
    }
  );

  // ຕຽມຂໍ້ມູນສຳລັບ charts
  const chartData = useMemo(() => {
    if (!revenueData?.data) return [];

    return revenueData.data.groupedData.map((item) => ({
      name: dayjs(item.date).format("DD/MM"),
      revenue: item.revenue.LAK,
      receiptCount: item.receiptCount,
    }));
  }, [revenueData]);

  // ຕຽມຂໍ້ມູນສຳລັບ pie chart (ໃຊ້ຂໍ້ມູນຈາກ room usage)
  const serviceData = useMemo(() => {
    if (!roomUsageData?.data) return [];

    const totalServices = roomUsageData.data.summary.totalServices;
    return roomUsageData.data.roomUsage.map((room, index) => ({
      name: room.room.roomName,
      value: Math.round((room.totalServices / totalServices) * 100),
      color: COLORS[index % COLORS.length],
    }));
  }, [roomUsageData]);

  // ສະຖິຕິຫຼັກສຳລັບ dashboard
  const dashboardStats = useMemo(() => {
    const todayRevenue = revenueData?.data?.summary?.totalRevenue?.LAK || 0;
    const todayBookings = revenueData?.data?.summary?.totalReceipts || 0;
    const newCustomers =
      customerAnalysisData?.data?.customerAnalysis?.newCustomers || 0;
    const completedServices = roomUsageData?.data?.summary?.totalServices || 0;

    return {
      todayRevenue,
      todayBookings,
      newCustomers,
      completedServices,
    };
  }, [revenueData, customerAnalysisData, roomUsageData]);

  // ຕຽມຂໍ້ມູນສຳລັບຕາຕະລາງການຈອງມື້ນີ້
  const todayBookings = useMemo(() => {
    if (!todayBookingsData?.data) return [];
    return todayBookingsData.data.map((booking, index) => {
      return {
        key: booking._id || String(index),
        date: booking.scheduledDate,
        time: booking.scheduledTime,
        customer: booking.customer?.name || "N/A",
        service: booking.course?.name || "N/A",
        therapist: booking.assignedTherapists?.[0]?.name || "N/A",
        room: booking.assignedRoom?.roomName || "N/A",
        status: booking.status || "N/A",
      };
    });
  }, [todayBookingsData]);

  // Top therapists ຈາກຂໍ້ມູນ employee performance
  const topTherapists = useMemo(() => {
    if (!employeePerformanceData?.data) return [];

    return employeePerformanceData.data.performance
      .filter((emp) => emp.employee.role === "therapist")
      .slice(0, 3)
      .map((emp) => ({
        name: emp.employee.name,
        services: emp.totalServices,
        satisfaction: emp.customerSatisfactionAvg || 0,
      }));
  }, [employeePerformanceData]);

  // ຄໍລຳສຳລັບຕາຕະລາງ
  const columns = [
    {
      title: "ວັນທີ",
      dataIndex: "date",
      key: "date",
      render: (date: string) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: "ເວລາ",
      dataIndex: "time",
      key: "time",
      width: 80,
    },
    {
      title: "ລູກຄ້າ",
      dataIndex: "customer",
      key: "customer",
    },
    {
      title: "ບໍລິການ",
      dataIndex: "service",
      key: "service",
    },
    {
      title: "ໝໍນວດ",
      dataIndex: "therapist",
      key: "therapist",
    },
    {
      title: "ຫ້ອງ",
      dataIndex: "room",
      key: "room",
    },
    {
      title: "ສະຖານະ",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => {
        const statusConfig = {
          pending: { color: "orange", text: "ລໍຖ້າຢືນຢັນ" },
          confirmed: { color: "blue", text: "ຢືນຢັນແລ້ວ" },
          in_progress: { color: "green", text: "ກຳລັງດຳເນີນການ" },
          completed: { color: "default", text: "ສຳເລັດ" },
        };
        const config = statusConfig[status as keyof typeof statusConfig] || {
          color: "default",
          text: status,
        };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "ຖ້າດຳເນີນການ",
      key: "action",
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          {record.status === "pending" && (
            <Button
              type="primary"
              size="small"
              loading={confirmBookingMutation.isLoading}
              onClick={() => confirmBookingMutation.mutate(record.key)}
            >
              ຢືນຢັນ
            </Button>
          )}
          {["pending", "confirmed"].includes(record.status) && (
            <Button
              type="default"
              size="small"
              loading={cancelBookingMutation.isLoading}
              onClick={() => cancelBookingMutation.mutate(record.key)}
            >
              ຍົກເລິກ
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const isLoading =
    revenueLoading ||
    roomUsageLoading ||
    employeePerformanceLoading ||
    customerAnalysisLoading ||
    todayBookingsLoading;

  const hasError =
    revenueError ||
    roomUsageError ||
    employeePerformanceError ||
    customerAnalysisError ||
    todayBookingsError;

  if (hasError) {
    return (
      <div>
        <Title level={2}>ໜ້າຫຼັກ</Title>
        <Alert
          message="ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ"
          description="ກະລຸນາລອງໃໝ່ອີກຄັ້ງ ຫຼື ຕິດຕໍ່ຜູ້ດູແລລະບົບ"
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      <Title level={2}>ໜ້າຫຼັກ</Title>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* ສະຖິຕິລວມ */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="ລາຍຮັບມື້ນີ້"
                value={dashboardStats.todayRevenue}
                suffix="₭"
                valueStyle={{ color: "#3f8600" }}
                prefix={isLoading ? <LoadingOutlined /> : <DollarOutlined />}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="ການຈອງມື້ນີ້"
                value={dashboardStats.todayBookings}
                valueStyle={{ color: "#1890ff" }}
                prefix={isLoading ? <LoadingOutlined /> : <CalendarOutlined />}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="ລູກຄ້າໃໝ່"
                value={dashboardStats.newCustomers}
                valueStyle={{ color: "#722ed1" }}
                prefix={isLoading ? <LoadingOutlined /> : <UserOutlined />}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="ບໍລິການທີ່ໃຊ້ແລ້ວ"
                value={dashboardStats.completedServices}
                valueStyle={{ color: "#52c41a" }}
                prefix={isLoading ? <LoadingOutlined /> : <TrophyOutlined />}
                loading={isLoading}
              />
            </Card>
          </Col>
        </Row>

        {/* ກຣາຟແລະສະຖິຕິ */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="ລາຍຮັບປະຈຳວັນ (LAK)" style={{ height: 400 }}>
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [
                        new Intl.NumberFormat("lo-LA").format(value as number) +
                          " ₭",
                        "ລາຍຮັບ",
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#1890ff"
                      strokeWidth={2}
                      dot={{ fill: "#1890ff", strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="ສັດສ່ວນການໃຊ້ຫ້ອງ" style={{ height: 400 }}>
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={serviceData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                    >
                      {serviceData.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, "ສັດສ່ວນ"]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
        </Row>

        {/* ການຈອງມື້ນີ້ແລະນັກບຳບັດຍອດນິຍົມ */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={24}>
            <Card
              title={
                <Space>
                  <ClockCircleOutlined />
                  ລາຍການຈອງມື້ນີ້ ({today})
                </Space>
              }
            >
              {todayBookingsError ? (
                <Alert
                  message="ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຈອງມື້ນີ້"
                  description="ກະລຸນາລອງໃໝ່ອີກຄັ້ງ ຫຼື ຕິດຕໍ່ຜູ້ດູແລລະບົບ"
                  type="error"
                  showIcon
                />
              ) : (
                <Table
                  columns={columns}
                  dataSource={todayBookings}
                  pagination={false}
                  size="small"
                  loading={todayBookingsLoading}
                />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={24}>
            <Card title="ຊ່າງຍອດຮິດ">
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 200,
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : (
                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="middle"
                >
                  {topTherapists.map((therapist, index) => (
                    <div key={index}>
                      <div style={{ marginBottom: 8 }}>
                        <Space
                          style={{
                            width: "100%",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>
                            {therapist.name}
                          </span>
                          <span style={{ fontSize: "12px", color: "#666" }}>
                            {therapist.services} ບໍລິການ
                          </span>
                        </Space>
                      </div>
                      <Progress
                        percent={therapist.satisfaction}
                        size="small"
                        format={(percent) => `${percent}% ພຶງພໍໃຈ`}
                      />
                    </div>
                  ))}
                </Space>
              )}
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default DashboardPage;